import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'src/hooks/useAuth';
import { InterviewerService } from '../InterviewerService';

// `enabled` lets a caller defer the request (e.g. the two non-default tabs on
// UpcomingInterviews only fetch once the interviewer actually switches to
// them, instead of firing all three status queries on page load).
// search/fromDatetime/toDatetime are server-side filters (the backend's own
// route takes them directly) — included in the queryKey so a change refetches
// rather than silently reusing a differently-filtered cache entry.
export const useInterviewerInterviews = (
  status,
  {
    page = 1,
    pageSize = 10,
    search,
    fromDatetime,
    toDatetime,
    enabled = true,
  } = {}
) =>
  useQuery({
    queryKey: [
      'interviewer',
      'interviews',
      status,
      page,
      pageSize,
      search,
      fromDatetime,
      toDatetime,
    ],
    queryFn: () =>
      InterviewerService.listInterviews(status, page, pageSize, {
        search,
        fromDatetime,
        toDatetime,
      }),
    enabled: !!status && enabled,
    select: (response) =>
      response?.data ?? { interviews: [], pagination: null },
  });

// Requirement + candidate + match + interview + timeline + scorecards +
// sibling-interview list for one interview_round_detail_id — the Interview
// Brief page's main data source.
export const useInterviewBrief = (interviewRoundDetailId) =>
  useQuery({
    queryKey: ['interviewer', 'interview-brief', interviewRoundDetailId],
    queryFn: () => InterviewerService.getBrief(interviewRoundDetailId),
    enabled: !!interviewRoundDetailId,
    select: (response) => response?.data,
  });

// The generated verify/clarify question bank for that same interview.
export const useInterviewQuestionnaire = (interviewRoundDetailId) =>
  useQuery({
    queryKey: [
      'interviewer',
      'interview-questionnaire',
      interviewRoundDetailId,
    ],
    queryFn: () => InterviewerService.getQuestionnaire(interviewRoundDetailId),
    enabled: !!interviewRoundDetailId,
    select: (response) => response?.data,
  });

// The interviewer's own private sticky notes for this interview. The API
// returns `data.notes` as an array — one entry per interviewer who has ever
// left a note here, e.g. a panel interview has one per panelist — so this
// can't just take index 0 (that's whichever panelist's entry the backend
// happens to list first, not necessarily the caller's own). Match on
// `interviewer_id` against the logged-in user's own id instead (`cid` in
// the raw localStorage user object, exposed as `.id` by useAuth's
// getUserInfo() — see AuthContext.js's transformUserData) before unwrapping
// to `{ comments }`. `enabled` lets the Interview Brief page defer the
// fetch until the notes widget is actually opened, same gating this file's
// other on-visit queries already use.
export const useInterviewNotes = (interviewRoundDetailId, enabled = true) => {
  const { getUserInfo } = useAuth();
  const viewerInterviewerId = getUserInfo()?.id ?? null;
  return useQuery({
    queryKey: ['interviewer', 'interview-notes', interviewRoundDetailId],
    queryFn: () => InterviewerService.getNotes(interviewRoundDetailId),
    enabled: !!interviewRoundDetailId && enabled,
    select: (response) => {
      const notes = response?.data?.notes || [];
      return (
        notes.find((note) => note.interviewer_id === viewerInterviewerId) ||
        null
      );
    },
  });
};
